import React, { useEffect } from 'react';
import { PageHeader } from '../components/layout/Header';
import { useAppContext } from '../contexts/AppContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { usePermissions } from '../hooks/usePermissions';
import MetaIntegrationModal from '../features/settings/components/MetaIntegrationModal';
import GoogleAdsConnectModal from '../features/settings/components/GoogleAdsConnectModal';
import GoogleAdsCard from '../features/settings/components/GoogleAdsCard';
import MetaAdsCard from '../features/settings/components/MetaAdsCard';
import { useMetaConnection } from '../features/settings/hooks/useMetaConnection';
import { useGoogleConnection } from '../features/settings/hooks/useGoogleConnection';
import { useSubscription } from '../features/subscription/hooks/useSubscription';
import { isMetaOAuthFlowActive } from '../features/onboarding/metaReturn';

const TrackingSettingsContent: React.FC = () => {
    const { currentOrganization } = useOrganization();
    const { stripePlans, stripeSubscription } = useSubscription();
    const meta = useMetaConnection(currentOrganization?.id);
    const google = useGoogleConnection(currentOrganization?.id);
    const plan = stripePlans.find((item) => item.id === stripeSubscription?.planId);
    const googleAdsAvailability = plan?.features.googleAds ?? 'unavailable';

    return (
        <main className="flex flex-col gap-0">
            <PageHeader
                title="Integrações"
                subtitle="Conecte Meta Ads e Google Ads para atribuir leads e conversões."
                hideBackButton
            />

            <section
                aria-label="Provedores de anúncios"
                className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2"
            >
                <GoogleAdsCard
                    status={google.status}
                    connecting={google.connecting}
                    onConnect={google.connect}
                    onDisconnect={google.disconnect}
                    onReconnect={google.reconnect}
                    availability={googleAdsAvailability}
                />

                <MetaAdsCard
                    state={meta.state}
                    onConnect={meta.connect}
                    onDisconnect={meta.disconnect}
                    onReconnect={meta.reconnect}
                    formatExpiryDate={meta.formatExpiryDate}
                />
            </section>

            {meta.modalData ? (
                <MetaIntegrationModal
                    isOpen={meta.isModalOpen}
                    onClose={meta.closeModal}
                    onSave={meta.save}
                    accessToken={meta.modalData.accessToken}
                    accounts={meta.modalData.accounts}
                    pages={meta.modalData.pages}
                    isLoading={meta.isSaving}
                />
            ) : null}

            {google.modalData ? (
                <GoogleAdsConnectModal
                    isOpen={google.isModalOpen}
                    onClose={google.closeModal}
                    customers={google.modalData.customers}
                    actions={google.actions}
                    loadingActions={google.loadingActions}
                    isSaving={google.saving}
                    onCustomerChange={google.loadActionsForCustomer}
                    onSave={google.save}
                />
            ) : null}
        </main>
    );
};

/**
 * Gate de permissão: só redireciona depois que a org/role carregar,
 * e nunca durante o retorno OAuth Meta (senão perde ?code=&state=).
 */
const TrackingSettingsPage: React.FC = () => {
    const { setCurrentPage } = useAppContext();
    const { isLoading: orgLoading } = useOrganization();
    const { canManageIntegrations } = usePermissions();

    useEffect(() => {
        if (orgLoading) return;
        if (isMetaOAuthFlowActive()) return;
        if (!canManageIntegrations) setCurrentPage('dashboard');
    }, [orgLoading, canManageIntegrations, setCurrentPage]);

    if (orgLoading) return null;

    if (!canManageIntegrations && !isMetaOAuthFlowActive()) return null;

    return <TrackingSettingsContent />;
};

export default TrackingSettingsPage;
